import { useEffect, useState } from "react";
import uniqBy from "lodash/uniqBy";
import createUseContext from "constate";
import moment from "moment";

import Helpers from "../../services/helpers";
import { SAVED_SEARCH_TYPES } from "../../globals";
import { usePopularChartContext } from "./popularchartcontext";
import { useCardsContext } from "./cardcontext";
import { useUserContext } from "./usercontext";
import useGlobal from "../../store";
import { QUERY_RANGE_DATES } from "../charts/constants";
import { isUserOfMembershipTier, MembershipTiers } from "utils/user"; 

const { mapEntitiesToSelect } = Helpers;

type Option = {
  label?: string;
  value: string;
  id: string;
};

function useSearch({ range }) {
  const [autoFetch, setAutoFetch] = useState(false);
  const [searchCardId, setSearchCardId] = useState(null);
  const [searchSealedWaxId, setSearchSealedWaxId] = useState(null);
  const [isSearchRunning, setIsSearchRunning] = useState(false);
  const [initialSearchPerformed, setInitialSearchPerformed] = useState(false);

  const { user } = useUserContext();

  const defaultRange = isUserOfMembershipTier(user, MembershipTiers.STARTER) ? "30" : "90";

  // Date Range
  const [dateRangeLabel, setDateRangeLabel] = useState(
    QUERY_RANGE_DATES[range] ? QUERY_RANGE_DATES[range].label : defaultRange,
  );
  const [startDate, setStartDate] = useState(
    QUERY_RANGE_DATES[range] ? QUERY_RANGE_DATES[range].value : moment().subtract(parseInt(defaultRange), "days"),
  );
  const [endDate, setEndDate] = useState(moment());
  const [dateRangeSearched, setDateRangeSearched] = useState([]);
  const [searchDisabled, setSearchDisabled] = useState(true);

  // players
  const [playersOriginal, setPlayersOriginal] = useState([]);
  const [players, setPlayers] = useState([]);
  const [playersSelected, setPlayersSelected] = useState([]);
  const [playersAvailable, setPlayersAvailable] = useState([]);
  // TODO: find out why `playersFiltered` is not used
  const [playersFiltered, setPlayersFiltered] = useState([]);

  // sets
  const [allSetsOriginal, setAllSetsOriginal] = useState([]);
  const [sets, setSets] = useState([]);
  const [setsSelected, setSetsSelected] = useState([]);
  const [setsAvailable, setSetsAvailable] = useState([]);

  // years
  const [years, setYears] = useState([]);
  const [yearsSelected, setYearsSelected] = useState([]);
  const [yearsAvailable, setYearsAvailable] = useState([]);

  //grades
  const [gradesOriginal, setGradesOriginal] = useState([]);
  const [grades, setGrades] = useState([]);
  const [gradesSelected, setGradesSelected] = useState([]);
  const [gradesAvailable, setGradesAvailable] = useState([]);

  //variations
  const [variationsOriginal, setVariationsOriginal] = useState([]);
  const [variations, setVariations] = useState([]);
  const [variationsSelected, setVariationsSelected] = useState([]);
  const [variationsAvailable, setVariationsAvailable] = useState([]);
  const [setVariationIdsGroupedByName, setSetVariationIdsGroupedByName] = useState([]);

  //sports
  const [sportsOriginal, setSportsOriginal] = useState([]);
  const [sports, setSports] = useState([]);
  const [sportsSelected, setSportsSelected] = useState([]);
  const [sportsAvailable, setSportsAvailable] = useState([]);

  // set / years
  const [setNameYears, setSetNameYears] = useState([]);
  const [setNameYearsSelected, setSetNameYearsSelected] = useState([]);
  const [setNameYearsAvailable, setSetNameYearsAvailable] = useState([]);
  const [setNameYearsOriginal, setSetNameYearsOriginal] = useState([]);

  // box types
  const [boxTypes, setBoxTypes] = useState([]);
  const [boxTypesSelected, setBoxTypesSelected] = useState([]);
  const [boxTypesAvailable, setBoxTypesAvailable] = useState([]);
  const [boxTypesOriginal, setBoxTypesOriginal] = useState([]);

  const [popularCardStats, setPopularCardStats] = useState({});
  const [groupedPopularCardStats, setGroupedPopularCardStats] = useState({});

  const [popularSealedWaxStats, setPopularSealedWaxStats] = useState({});
  const [groupedPopularSealedWaxStats, setGroupedPopularSealedWaxStats] = useState({});

  const [parsedCards, setParsedCards] = useState([]);
  const { cards } = useCardsContext();
  const [globalState] = useGlobal();

  const originals: { [id: string]: any[] } = {
    sports: sportsOriginal,
    players: playersOriginal,
    sets: allSetsOriginal,
    variations: variationsOriginal,
    grades: gradesOriginal,
  };

  const selections: { [id: string]: Option[] } = {
    sports: sportsSelected,
    players: playersSelected,
    sets: setsSelected,
    years: yearsSelected,
    variations: variationsSelected,
    grades: gradesSelected,
    setNameYears: setNameYearsSelected,
    boxTypes: boxTypesSelected,
  };

  const selectionsStrings: { [id: string]: string } = {
    sports: "sportsSelected",
    players: "playersSelected",
    sets: "setsSelected",
    years: "yearsSelected",
    variations: "variationsSelected",
    grades: "gradesSelected",
    setNameYears: "setNameYearsSelected",
    boxTypes: "boxTypesSelected",
  };

  const getParamsToApi = (parentFields: string[], options: { [id: string]: any } = {}) => {
    const DATE_FORMAT = "YYYY-MM-DD";
    const params: { [id: string]: any } = {
      startDate: startDate.format(DATE_FORMAT),
      endDate: endDate.format(DATE_FORMAT),
    };

    Object.keys(selections).forEach((field: string) => {
      const selected: Option[] = options.context ? options.context[selectionsStrings[field]] : selections[field];

      if (!parentFields || parentFields.indexOf(field) > -1) {
        if (field === "variations" && options.isSetVariation) {
          params["set_variations"] = getGroupedSVByVariationName();
        } else params[field] = selected.map((i) => i.value);
      }
    });

    if (options.cardId || options.sealedWaxId) {
      params.id = options.cardId || options.sealedWaxId;
      return params;
    }

    if (options.rcOnly) params.rcOnly = true;

    switch (options.savedSearchType) {
      case SAVED_SEARCH_TYPES.PLAYER_CHARTS:
        params.searchType = "PCC";
        delete params.years;
        delete params.sets;
        break;
      case SAVED_SEARCH_TYPES.SET_AND_YEAR:
        params.searchType = "SYC";
        break;
      default:
        break;
    }

    if (options.returnRatios) params.returnRatios = true;

    return params;
  };

  const getParamsToApiFromName = (parentFields, options) => {
    const params: { [id: string]: any } = {
      startDate: startDate ? startDate.startOf("day") : null,
      endDate: endDate ? endDate.endOf("day") : null,
    };

    if (options.cardId) {
      params.id = options.cardId;
      return params;
    }

    Object.keys(originals).forEach((field: string) => {
      if (field === "years") return (params.years = yearsSelected);

      const selected: Option[] = selections[field];
      const original: any[] = originals[field];

      if (!parentFields || parentFields.indexOf(field) > -1) {
        params[field] = selected
          .map((name) => {
            const match = original.find((i) => i.name === name);
            if (match) return match.id;
          })
          .filter((i) => i);
      }
    });

    return params;
  };

  const getParamsToGraph = () => {
    const params: { [id: string]: any } = {
      startDate: startDate.startOf("day"),
      endDate: endDate.endOf("day"),
      playersSelected,
    };

    return params;
  };

  const setEntitiesFromSearch = (entities) => {
    const update: { [id: string]: any } = {};
    const _mapPlayer = (arr) => arr.map(({ id, name, notes }) => ({ label: name, value: id.toString(), notes }));

    if (entities.players) {
      const mapped = _mapPlayer(entities.players);
      setPlayersSelected(mapped);
      update.playersSelected = mapped;
    }
    if (entities.grades) {
      const mapped = mapEntitiesToSelect(entities.grades);
      setGrades(mapped);
      setGradesSelected(mapped);
      update.gradesSelected = mapped;
    }
    if (entities.sets) {
      entities.sets = entities.sets.map((item) => ({ ...item, name: Helpers.generateSetName(item) }));
      const mapped = mapEntitiesToSelect(entities.sets);
      setSets(mapped);
      setSetNameYears(mapped);
      setSetsSelected(mapped);
      setSetNameYearsSelected(mapped);
      update.setNameYearsSelected = mapped;
      update.setsSelected = mapped;

      const years = uniqBy(
        entities.sets.map((set) => ({
          label: set.year.toString(),
          value: set.year.toString(),
        })),
        "label",
      );

      setYears(years);
      setYearsSelected(years);
      update.yearsSelected = years;
    }

    if (entities.set_variations) {
      const { uniq, grouped } = Helpers.generateSetVariationsOptions(
        entities.set_variations.map((sv) => ({
          label: Helpers.generateSetVariationName(sv),
          value: sv.id,
        })),
      );

      setVariations(uniq);
      setVariationsSelected(uniq);
      setSetVariationIdsGroupedByName(grouped);
    }

    if (entities.sports) {
      const mapped = mapEntitiesToSelect(entities.sports);
      setSportsSelected(mapped);
      update.sportsSelected = mapped;
    }

    if (entities.box_types) {
      const mapped = mapEntitiesToSelect(entities.box_types);
      setBoxTypesSelected(mapped);
      update.boxTypesSelected = mapped;
    }

    return update;
  };

  const getGroupedSVByVariationName = () => {
    const selected = [];
    variationsSelected.forEach((option: Option) => {
      const setVariationsGroup = setVariationIdsGroupedByName[option.label] || [];
      selected.push(...setVariationsGroup);
    });

    return selected;
  };

  const reset = () => {
    setAutoFetch(false);
    setYearsSelected([]);
    setSetsSelected([]);
    setGradesSelected([]);
    setVariationsSelected([]);
    setSportsSelected([]);
    setPlayersSelected([]);
    setSetNameYearsSelected([]);
    setBoxTypesSelected([]);
    setInitialSearchPerformed(false);
    setParsedCards([]);
    setSearchCardId(null);
    setSearchSealedWaxId(null);
    setStartDate(moment().subtract(parseInt(defaultRange), "days"));
    setEndDate(moment());
    setGroupedPopularCardStats({});
    setPopularCardStats({});
    setGroupedPopularSealedWaxStats({});
    setPopularSealedWaxStats({});
  };

  useEffect(() => {
    if (cards) {
      setParsedCards(Helpers.parseCards(cards, startDate, endDate, globalState));
    }
  }, [cards, startDate, endDate, globalState]);

  const popularChartContext = usePopularChartContext();

  const groupFn = (popularStats) =>
    Object.values(popularStats).reduce(
      (acc, val) => {
        // @ts-ignore
        acc.count += val.count;
        acc.total += val.total;
        acc.min = Math.min(acc.min, val.low);
        acc.max = Math.max(acc.max, val.high);
        acc.minCount = Math.min(acc.minCount, val.minCount);
        acc.maxCount = Math.max(acc.maxCount, val.maxCount);
        acc.minPercentChange = Math.min(acc.minPercentChange, val.changePercent);
        acc.maxPercentChange = Math.min(acc.maxPercentChange, val.changePercent);
        return acc;
      },
      {
        count: 0,
        total: 0,
        min: Number.MAX_VALUE,
        max: 0,
        minCount: Number.MAX_VALUE,
        maxCount: 0,
        minPercentChange: Number.MAX_VALUE,
        maxPercentChange: 0,
      },
    );

  useEffect(() => {
    const groupedStats = groupFn(popularCardStats);
    setGroupedPopularCardStats(groupedStats);
  }, [popularCardStats, popularChartContext.hiddenLines]);

  useEffect(() => {
    const groupedStats = groupFn(popularSealedWaxStats);
    setGroupedPopularSealedWaxStats(groupedStats);
  }, [popularSealedWaxStats, popularChartContext.hiddenLines]);

  return {
    isSearchRunning,
    setIsSearchRunning,
    reset,
    searchDisabled,
    setSearchDisabled,
    dateRangeSearched,
    setDateRangeSearched,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    players,
    setPlayers,
    playersSelected,
    setPlayersSelected,
    playersAvailable,
    setPlayersAvailable,
    sets,
    setSets,
    setsSelected,
    setSetsSelected,
    setsAvailable,
    setSetsAvailable,
    years,
    setYears,
    yearsSelected,
    setYearsSelected,
    yearsAvailable,
    setYearsAvailable,
    grades,
    setGrades,
    gradesSelected,
    setGradesSelected,
    gradesAvailable,
    setGradesAvailable,
    variations,
    setVariations,
    variationsSelected,
    setVariationsSelected,
    variationsAvailable,
    setVariationsAvailable,
    sports,
    setSports,
    sportsSelected,
    setSportsSelected,
    sportsAvailable,
    setSportsAvailable,
    setNameYears,
    setSetNameYears,
    setNameYearsSelected,
    setSetNameYearsSelected,
    setNameYearsAvailable,
    setSetNameYearsAvailable,
    initialSearchPerformed,
    setInitialSearchPerformed,
    gradesOriginal,
    setGradesOriginal,
    variationsOriginal,
    setVariationsOriginal,
    sportsOriginal,
    setSportsOriginal,
    playersOriginal,
    setPlayersOriginal,
    getParamsToApi,
    getParamsToGraph,
    setEntitiesFromSearch,
    allSetsOriginal,
    setAllSetsOriginal,
    getParamsToApiFromName,
    setPlayersFiltered,
    dateRangeLabel,
    setDateRangeLabel,
    parsedCards,
    setVariationIdsGroupedByName,
    setSetVariationIdsGroupedByName,
    getGroupedSVByVariationName,
    autoFetch,
    setAutoFetch,
    searchCardId,
    setSearchCardId,
    setNameYearsOriginal,
    setSetNameYearsOriginal,
    popularCardStats,
    setPopularCardStats,
    groupedPopularCardStats,
    boxTypes,
    setBoxTypes,
    boxTypesSelected,
    setBoxTypesSelected,
    boxTypesAvailable,
    setBoxTypesAvailable,
    boxTypesOriginal,
    setBoxTypesOriginal,
    searchSealedWaxId,
    setSearchSealedWaxId,
    popularSealedWaxStats,
    setPopularSealedWaxStats,
    groupedPopularSealedWaxStats,
    setGroupedPopularSealedWaxStats,
  };
}

const [SearchProvider, useSearchContext] = createUseContext(useSearch);

export { SearchProvider, useSearchContext };
